import { Table } from "@optimizely/axiom";
import React, { useEffect, useState } from "react";
import { ADMINCENTER_PRODUCT_ID } from "../../../../constants/constants";
import { ProductPermission } from "../../../../domain/Permission.interface";
import { AccessManagementUserFormInstanceTableRow } from "./AccessManagementUserFormInstanceTableRow";
import { useAccessFlowUserFormContext } from "./AccessManagementUserFormProvider";
import { Role } from "../../../../domain/Role";
import { UserGroup } from "../../../../domain/UserGroup";
import { ProductAttribute } from "../../../../domain/ProductAttribute";
import { IOrganizationProductInstance } from "../../../../domain/OrganizationProduct";
import { useLocation } from "react-router-dom";

type AvailableRole = {
    group: UserGroup;
    role: Role;
    project?: ProductAttribute | undefined;
};

type GroupRoleRow = {
    groupRole: AvailableRole;
    instance: IOrganizationProductInstance;
};

export const AccessManagementUserFormInstanceTable = ({
    className = "",
    productAccess,
    productAvailability,
    onGroupsChanged
}: {
    className?: string;
    productAccess?: ProductPermission;
    productAvailability: ProductPermission | undefined;
    onGroupsChanged: (previousGroupId: string | undefined, updatedGroupId: string | undefined) => Promise<any | void>;
}) => {
    const { productId, instanceAccess: instances } = productAccess || {};
    const [groupRoleRows, setGroupRoleRows] = useState<GroupRoleRow[]>([]);
    const { userGroupIds, setUserGroupIds } = useAccessFlowUserFormContext();
    const areAdminCenterInstances = productId === ADMINCENTER_PRODUCT_ID;
    const areExperimentationInstances = productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;

    const { pathname } = useLocation();
    const isOnUsersPage = pathname === "/user-management/users";

    const handleGroupChanged = async (previousGroupId: string | undefined, updatedGroupId: string | undefined) => {
        let updatedRows = [...groupRoleRows];
        let updatedGroupIds = [...userGroupIds];

        updatedRows = updatedRows.filter((r) => r.groupRole.group.id !== previousGroupId);
        updatedGroupIds = updatedGroupIds.filter((id) => id !== previousGroupId);

        if (updatedGroupId) {
            // Find the instanceAccess entry that contains the role with the matching group ID
            // This ensures the instance and role come from the same source
            const matchingInstanceAccess = productAvailability?.instanceAccess.find((ia) =>
                ia.availableRoles.find((ar) => ar.group.id === updatedGroupId)
            );

            if (matchingInstanceAccess) {
                const instance = matchingInstanceAccess.instance;
                const groupRole = matchingInstanceAccess.availableRoles.find((ar) => ar.group.id === updatedGroupId);

                if (instance && groupRole) {
                    updatedRows = [
                        ...updatedRows,
                        {
                            instance,
                            groupRole
                        }
                    ];

                    updatedGroupIds = [...updatedGroupIds, groupRole.group.id];
                }
            }
        }
        const result = await onGroupsChanged(previousGroupId, updatedGroupId);

        setGroupRoleRows(updatedRows);
        setUserGroupIds(updatedGroupIds);

        return result;
    };

    useEffect(() => {
        const initialRows = instances?.map(({ instance, availableRoles }) => {
            const instanceRoleRows: GroupRoleRow[] = [];
            availableRoles.forEach((availableRole) => {
                if (
                    !instanceRoleRows?.some(
                        (r) =>
                            r.groupRole.group.id === availableRole.group.id &&
                            r.groupRole.role.id === availableRole.role.id
                    )
                ) {
                    const groupRow: GroupRoleRow = {
                        groupRole: availableRole,
                        instance
                    };
                    instanceRoleRows.push(groupRow);
                }
            });
            return instanceRoleRows;
        });
        !!initialRows && setGroupRoleRows(initialRows.flat());
    }, [instances]);

    const subsequentInstanceRow = ({
        rows,
        rowIndex,
        instance
    }: {
        rows: GroupRoleRow[];
        instance: any;
        rowIndex: number;
    }) => {
        const previous = rows.slice(0, rowIndex);
        return !!previous.find((i) => i.instance.id === instance.id);
    };

    const existingRoleIds = groupRoleRows.map((r) => r.groupRole.role.id);

    return (
        <Table
            className={className}
            // eslint-disable-next-line react/style-prop-object
            style="rule"
            shouldAddHover={false}
            tableLayoutAlgorithm="auto"
        >
            <Table.THead>
                <Table.TR>
                    <Table.TH sorting={{}} width={areExperimentationInstances ? "25%" : "40%"}>
                        {areAdminCenterInstances ? "Organization" : "Instance "}
                    </Table.TH>
                    {areExperimentationInstances && (
                        <Table.TH sorting={{}} width="25%">
                            Project
                        </Table.TH>
                    )}
                    <Table.TH sorting={{}} width={areExperimentationInstances ? "30%" : "40%"}>
                        Role
                    </Table.TH>
                    <Table.TH sorting={{}} width="20%"></Table.TH>
                </Table.TR>
            </Table.THead>
            <Table.TBody>
                {groupRoleRows?.map(({ instance, groupRole }: GroupRoleRow, index) => {
                    return (
                        <AccessManagementUserFormInstanceTableRow
                            key={`${instance.id}-${groupRole.group.id}`}
                            instance={instance}
                            existingRoleIds={existingRoleIds}
                            hideInstanceName={subsequentInstanceRow({ rows: groupRoleRows, rowIndex: index, instance })}
                            groupRole={groupRole}
                            groupRoleIndex={index}
                            productAvailability={productAvailability}
                            onGroupChanged={handleGroupChanged}
                            topLevelDelete={isOnUsersPage}
                        />
                    );
                })}
            </Table.TBody>
        </Table>
    );
};

AccessManagementUserFormInstanceTable.displayName = "AccessManagementUserFormInstanceTable";
